/**
 * Nesting rules for container blocks (spec §3/5, §6 fáze 3).
 *
 * Single source of the v2 placement policy: Sections live only at the
 * content root, Columns only inside a Section, plain blocks (including
 * shared-block references) anywhere. Enforced three times — when the
 * shell computes the allowed drop targets for a drag, when the outline
 * hit-tests a row, and defensively inside the store mutations.
 *
 * Pure module: importable by the store factory and by Vitest without a
 * component or registry context. Slot counts mirror the PHP types
 * (Section::slotCount, Columns::slotCount incl. the default of 2).
 */

import type { AllowedDropTargets } from './protocol';
import type { BlockNode } from './types';
import { isSharedBlock, markerId } from './types';

export const SECTION_SLUG = 'section';
export const COLUMNS_SLUG = 'columns';

const COLUMNS_DEFAULT = 2;
const COLUMNS_MAX = 6;

export function isContainer(block: BlockNode): boolean {
    if (isSharedBlock(block)) {
        return false;
    }

    return block.type === SECTION_SLUG || block.type === COLUMNS_SLUG;
}

/** Slot count of a container block, 0 for leaf types. */
export function slotCountOf(block: BlockNode): number {
    if (isSharedBlock(block)) {
        return 0;
    }

    if (block.type === SECTION_SLUG) {
        return 1;
    }

    if (block.type !== COLUMNS_SLUG) {
        return 0;
    }

    const raw = Number((block.value ?? {}).cols ?? COLUMNS_DEFAULT);
    const cols = Number.isFinite(raw) ? Math.trunc(raw) : COLUMNS_DEFAULT;

    return Math.max(1, Math.min(COLUMNS_MAX, cols));
}

/** Whether the block may become a child of the parent (null = content root). */
export function canPlace(block: BlockNode, parent: BlockNode | null): boolean {
    if (isSharedBlock(block)) {
        return true;
    }

    if (block.type === SECTION_SLUG) {
        return parent === null;
    }

    if (block.type === COLUMNS_SLUG) {
        return parent !== null && !isSharedBlock(parent) && parent.type === SECTION_SLUG;
    }

    return true;
}

/**
 * Drop targets legal for every dragged block — sent to the bridge on
 * dragStart so its hit-testing stays purely geometric. Dragged subtrees
 * are skipped (a block cannot land inside itself).
 */
export function allowedTargetsFor(content: BlockNode[], dragged: BlockNode[]): AllowedDropTargets {
    const draggedIds = new Set(dragged.map(markerId).filter((id): id is string => id !== null));
    const parents: AllowedDropTargets['parents'] = [];

    const visit = (blocks: BlockNode[]): void => {
        for (const block of blocks) {
            const id = markerId(block);

            if (id !== null && draggedIds.has(id)) {
                continue;
            }

            if (id !== null && isContainer(block) && dragged.every((entry) => canPlace(entry, block))) {
                parents.push({ id, slots: Array.from({ length: slotCountOf(block) }, (_, slot) => slot) });
            }

            if (block.children?.length) {
                visit(block.children);
            }
        }
    };

    visit(content);

    return { root: dragged.every((entry) => canPlace(entry, null)), parents };
}

/** Wrap-in-section applies to root-level, non-Section blocks only. */
export function canWrapInSection(content: BlockNode[], ids: string[]): boolean {
    if (ids.length === 0) {
        return false;
    }

    return ids.every((id) => {
        const block = content.find((candidate) => markerId(candidate) === id);

        return block !== undefined && (isSharedBlock(block) || block.type !== SECTION_SLUG);
    });
}
